import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, ExternalLink, Quote, ThumbsUp, RefreshCw } from "lucide-react";
import { ReviewCard } from "@/components/ReviewCard";
import { SEO } from "@/components/SEO";
import { AggregateRatingJsonLd } from "@/components/AggregateRatingJsonLd";
import { useGoogleReviews } from "@/hooks/useGoogleReviews";

const GOOGLE_REVIEW_URL = "https://www.google.com/search?q=mediastore+niort+avis";

// Distribution estimée — utilisée tant que Google ne renvoie pas ce détail.
const RATING_DISTRIBUTION = [
  { stars: 5, percentage: 90 },
  { stars: 4, percentage: 7 },
  { stars: 3, percentage: 2 },
  { stars: 2, percentage: 1 },
  { stars: 1, percentage: 0 },
];

export default function ReviewsPage() {
  const { data, loading } = useGoogleReviews();
  const averageRating = data.rating || 4.9;
  const totalReviews = data.total || 71;
  const reviews = data.reviews || [];

  return (
    <div className="min-h-screen" data-testid="reviews-page">
      <SEO
        title={`Avis Clients Mediastore Niort — ${averageRating.toFixed(1)}/5 sur Google (${totalReviews} avis)`}
        description={`Découvrez ${totalReviews} avis Google vérifiés sur Mediastore Niort. Note ${averageRating.toFixed(1)}/5. Spécialiste Apple, réparation iPhone, Mac, ordinateur. Plus de 10 ans d'expérience à Niort.`}
        keywords="avis Mediastore Niort, avis Google Mediastore, témoignages clients réparation Niort, avis réparateur iPhone Niort, avis réparation ordinateur Niort, Mediastore avis Apple"
        path="/avis-clients"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Avis Clients", path: "/avis-clients" },
        ]}
      />
      <AggregateRatingJsonLd
        rating={averageRating}
        total={totalReviews}
        googleMapsUrl={data.google_maps_url}
      />

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-yellow-50 to-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full mb-6">
              <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
              <span className="font-semibold">Avis vérifiés Google</span>
              {data.source === "google" && (
                <span
                  className="ml-1 inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full"
                  data-testid="reviews-live-badge"
                >
                  <RefreshCw className="h-3 w-3" />
                  En direct
                </span>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Ce que disent <span className="text-yellow-600">nos clients</span>
            </h1>

            <p className="text-lg text-slate-600 mb-8">
              Depuis plus de 10 ans, nous mettons la satisfaction client au cœur de notre métier.
              Découvrez les avis de ceux qui nous ont fait confiance.
            </p>

            {/* Rating Summary */}
            <Card className="max-w-lg mx-auto" data-testid="rating-summary-card">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="text-center">
                    <div
                      className="text-6xl font-bold text-slate-900 mb-2"
                      data-testid="reviews-avg-rating"
                    >
                      {averageRating.toFixed(1)}
                    </div>
                    <div className="flex items-center justify-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map((position) => (
                        <Star
                          key={`hero-rating-star-${position}`}
                          className={`w-6 h-6 ${
                            position <= Math.round(averageRating)
                              ? "text-yellow-400 fill-yellow-400"
                              : "text-slate-300"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-slate-600" data-testid="reviews-total">
                      {totalReviews} avis
                    </p>
                  </div>

                  <div className="flex-1 w-full">
                    {RATING_DISTRIBUTION.map((item) => (
                      <div key={`dist-${item.stars}`} className="flex items-center gap-2 mb-1">
                        <span className="text-sm w-3">{item.stars}</span>
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <div className="flex-1 bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-yellow-400 h-2 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-slate-500 w-10">{item.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <div key={`skeleton-${i}`} className="h-48 skeleton rounded-xl"></div>
              ))
            ) : (
              reviews.map((review, index) => (
                <ReviewCard
                  key={`gr-${review.author}-${index}`}
                  review={{
                    id: `${review.author}-${index}`,
                    author_name: review.author,
                    rating: review.rating,
                    text: review.text,
                    relative_time: review.relative_publish_time,
                    profile_photo_url: review.profile_photo_url,
                  }}
                />
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <Quote className="w-12 h-12 mx-auto mb-6 text-yellow-400" />

            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Vous êtes satisfait de nos services ?
            </h2>

            <p className="text-lg text-slate-300 mb-8">
              Votre avis compte ! Partagez votre expérience et aidez d&apos;autres personnes
              à découvrir Mediastore.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 h-12 px-8 gap-2"
                  data-testid="leave-review-btn"
                >
                  <ThumbsUp className="h-5 w-5" />
                  Laisser un avis Google
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 h-12 px-8"
                >
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
